import { useMutation, useQueryClient } from '@tanstack/react-query'
import React from 'react'

import ActionButton from '@/components/misc/ActionButton'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import useAppSelector from '@/hooks/useAppSelector'
import useNotyf from '@/hooks/useNotyf'
import { useRqCertifiedAssessmentOrFail } from '@/hooks/useRqCertifiedAssessmentQuery'
import { useShowGenericServerError } from '@/hooks/useShowGenericServerError'
import { QUERY_KEYS } from '@/lib/queryKeys'
import rqCertifiedAssessmentService from '@/lib/services/rqCertifiedAssessmentService'
import PaymentDetails from '@/pages/rqCertifiedApplicationStatus/components/PaymentDetails'
import { selectCurrentCompanyOrFail } from '@/redux/slices/session'

interface Props {
  onHide: () => void
}

const MakePaymentClaimModal: React.FC<Props> = ({ onHide }) => {
  const currentCompany = useAppSelector(selectCurrentCompanyOrFail)
  const assessment = useRqCertifiedAssessmentOrFail()
  const notyf = useNotyf()
  const queryClient = useQueryClient()
  const showGenericServerError = useShowGenericServerError()
  const stage = assessment.currentStage

  const submitPaymentClaim = useMutation({
    mutationFn: async () =>
      rqCertifiedAssessmentService.submitPaymentClaim(currentCompany.id, {
        assessmentStage: stage,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.rqCertifiedAssessment,
      })
      notyf.success('Thank you!')
    },
    onError: showGenericServerError,
  })

  return (
    <Dialog open onHide={onHide}>
      <DialogHeader>
        <DialogTitle>Start your application</DialogTitle>
      </DialogHeader>

      <DialogBody>
        <>
          <p className="mb-4">
            {assessment.isAtStageOne ? (
              <InitialAssessmentInstructions />
            ) : (
              <FullAssessmentInstructions />
            )}
          </p>

          <p className="mb-4">
            <PaymentDetails />
          </p>

          <p className="mb-0">
            We will notify you when your payment details have been confirmed,
            and you can continue your application.
          </p>
        </>
      </DialogBody>

      <DialogFooter>
        <Button variant="outline-secondary" onClick={onHide}>
          Close
        </Button>

        <ActionButton
          isProcessing={submitPaymentClaim.isPending}
          variant="primary"
          onClick={() => {
            submitPaymentClaim.mutate()
          }}
        >
          I've paid
        </ActionButton>
      </DialogFooter>
    </Dialog>
  )
}

const InitialAssessmentInstructions: React.FC = () => {
  const currentCompany = useAppSelector(selectCurrentCompanyOrFail)

  return (
    <>
      Before you begin, there is an application processing fee of £450
      (Including VAT). Please use the details below with the payment reference "
      {currentCompany.id}-RQC-1".
    </>
  )
}

const FullAssessmentInstructions: React.FC = () => {
  const currentCompany = useAppSelector(selectCurrentCompanyOrFail)

  return (
    <>
      Before you begin, there is an application processing fee of £1,200
      (Including VAT) and once certified, there is an annual charge of £1,200
      thereafter. Please use the details below with the payment reference "
      {currentCompany.id}-RQC-2".
    </>
  )
}

export default MakePaymentClaimModal
